/**
 * Train Filters Component
 * Handles filtering functionality for train search results
 */

import { TrainFilters } from '../types/data-models';

export class TrainFiltersComponent {
  private container: HTMLElement;
  private filters: TrainFilters = {};
  private priceMinInput!: HTMLInputElement;
  private priceMaxInput!: HTMLInputElement;
  private operatorCheckboxes!: NodeListOf<HTMLInputElement>;
  private departureTimeCheckboxes!: NodeListOf<HTMLInputElement>;
  private durationMinInput!: HTMLInputElement;
  private durationMaxInput!: HTMLInputElement;
  private classCheckboxes!: NodeListOf<HTMLInputElement>;
  private clearAllButton!: HTMLButtonElement;
  private applyFiltersButton!: HTMLButtonElement;

  public onFiltersChange: ((filters: TrainFilters) => void) | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    this.initializeElements();
    this.bindEvents();
  }

  private initializeElements(): void {
    this.priceMinInput = this.container.querySelector('#price-min') as HTMLInputElement;
    this.priceMaxInput = this.container.querySelector('#price-max') as HTMLInputElement;
    this.operatorCheckboxes = this.container.querySelectorAll('input[name="operators"]');
    this.departureTimeCheckboxes = this.container.querySelectorAll('input[name="departure-times"]');
    this.durationMinInput = this.container.querySelector('#duration-min') as HTMLInputElement;
    this.durationMaxInput = this.container.querySelector('#duration-max') as HTMLInputElement;
    this.classCheckboxes = this.container.querySelectorAll('input[name="classes"]');
    this.clearAllButton = this.container.querySelector('.train-filters__clear-all') as HTMLButtonElement;
    this.applyFiltersButton = this.container.querySelector('.train-filters__apply') as HTMLButtonElement;
  }

  private bindEvents(): void {
    // Price range inputs
    if (this.priceMinInput) {
      this.priceMinInput.addEventListener('input', () => this.updateFilters());
    }
    if (this.priceMaxInput) {
      this.priceMaxInput.addEventListener('input', () => this.updateFilters());
    }

    // Operator checkboxes
    this.operatorCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => this.updateFilters());
    });

    // Departure time checkboxes
    this.departureTimeCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => this.updateFilters());
    });

    // Duration inputs
    if (this.durationMinInput) {
      this.durationMinInput.addEventListener('input', () => this.updateFilters());
    }
    if (this.durationMaxInput) {
      this.durationMaxInput.addEventListener('input', () => this.updateFilters());
    }

    // Class checkboxes
    this.classCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => this.updateFilters());
    });

    // Clear all button
    if (this.clearAllButton) {
      this.clearAllButton.addEventListener('click', () => this.clearAllFilters());
    }

    // Apply filters button
    if (this.applyFiltersButton) {
      this.applyFiltersButton.addEventListener('click', () => this.applyFilters());
    }
  }

  private updateFilters(): void {
    this.filters = {};

    // Price range
    const priceMin = this.priceMinInput?.value ? parseFloat(this.priceMinInput.value) : undefined;
    const priceMax = this.priceMaxInput?.value ? parseFloat(this.priceMaxInput.value) : undefined;
    
    if (priceMin !== undefined || priceMax !== undefined) {
      this.filters.priceRange = {
        min: priceMin || 0,
        max: priceMax || 1000,
        currency: 'EUR'
      };
    }

    // Operators
    const selectedOperators = Array.from(this.operatorCheckboxes)
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value);
    
    if (selectedOperators.length > 0) {
      this.filters.operators = selectedOperators;
    }

    // Departure time ranges
    const selectedTimeRanges = Array.from(this.departureTimeCheckboxes)
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value);
    
    if (selectedTimeRanges.length > 0) {
      this.filters.departureTimeRanges = selectedTimeRanges;
    }

    // Duration
    const durationMin = this.durationMinInput?.value ? parseInt(this.durationMinInput.value) : undefined;
    const durationMax = this.durationMaxInput?.value ? parseInt(this.durationMaxInput.value) : undefined;
    
    if (durationMin !== undefined || durationMax !== undefined) {
      this.filters.duration = {
        min: durationMin || 0,
        max: durationMax || 720 // 12 hours in minutes
      };
    }

    // Classes
    const selectedClasses = Array.from(this.classCheckboxes)
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value);
    
    if (selectedClasses.length > 0) {
      this.filters.class = selectedClasses;
    }

    this.updateFilterCount();
  }

  private updateFilterCount(): void {
    const filterCount = this.getActiveFilterCount();
    const countElement = this.container.querySelector('.train-filters__count') as HTMLElement;
    
    if (countElement) {
      if (filterCount > 0) {
        countElement.textContent = `${filterCount} filtre(s) actif(s)`;
        countElement.style.display = 'inline';
      } else {
        countElement.style.display = 'none';
      }
    }

    // Update clear all button state
    if (this.clearAllButton) {
      this.clearAllButton.disabled = filterCount === 0;
    }
  }

  private getActiveFilterCount(): number {
    let count = 0;
    
    if (this.filters.priceRange) count++;
    if (this.filters.operators && this.filters.operators.length > 0) count++;
    if (this.filters.departureTimeRanges && this.filters.departureTimeRanges.length > 0) count++;
    if (this.filters.duration) count++;
    if (this.filters.class && this.filters.class.length > 0) count++;
    
    return count;
  }

  private clearAllFilters(): void {
    // Clear price inputs
    if (this.priceMinInput) this.priceMinInput.value = '';
    if (this.priceMaxInput) this.priceMaxInput.value = '';

    // Clear operator checkboxes
    this.operatorCheckboxes.forEach(checkbox => {
      checkbox.checked = false;
    });

    // Clear departure time checkboxes
    this.departureTimeCheckboxes.forEach(checkbox => {
      checkbox.checked = false;
    });

    // Clear duration inputs
    if (this.durationMinInput) this.durationMinInput.value = '';
    if (this.durationMaxInput) this.durationMaxInput.value = '';

    // Clear class checkboxes
    this.classCheckboxes.forEach(checkbox => {
      checkbox.checked = false;
    });

    this.filters = {};
    this.updateFilterCount();
    this.applyFilters();
  }

  private applyFilters(): void {
    if (this.onFiltersChange) {
      this.onFiltersChange(this.filters);
    }
  }

  public setFilters(filters: TrainFilters): void {
    this.filters = { ...filters };

    // Update price inputs
    if (filters.priceRange) {
      if (this.priceMinInput) this.priceMinInput.value = filters.priceRange.min.toString();
      if (this.priceMaxInput) this.priceMaxInput.value = filters.priceRange.max.toString();
    }

    // Update operator checkboxes
    this.operatorCheckboxes.forEach(checkbox => {
      checkbox.checked = filters.operators?.includes(checkbox.value) || false;
    });

    // Update departure time checkboxes
    this.departureTimeCheckboxes.forEach(checkbox => {
      checkbox.checked = filters.departureTimeRanges?.includes(checkbox.value) || false;
    });

    // Update duration inputs
    if (filters.duration) {
      if (this.durationMinInput) this.durationMinInput.value = filters.duration.min.toString();
      if (this.durationMaxInput) this.durationMaxInput.value = filters.duration.max.toString();
    }

    // Update class checkboxes
    this.classCheckboxes.forEach(checkbox => {
      checkbox.checked = filters.class?.includes(checkbox.value) || false;
    });

    this.updateFilterCount();
  }

  public getFilters(): TrainFilters {
    return { ...this.filters };
  }

  public reset(): void {
    this.clearAllFilters();
  }
}